import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { PORTAL_ENABLED_DEFAULT } from '../config/portalConfig';

interface PortalState {
  enabled: boolean;
  ready: boolean;
}

let cached: PortalState | null = null;
const listeners: Set<(s: PortalState) => void> = new Set();

async function fetchPortalState(): Promise<PortalState> {
  const { data } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', 'portal_enabled')
    .maybeSingle();
  const enabled = data === null || data === undefined
    ? PORTAL_ENABLED_DEFAULT
    : data.value === 'true';
  return { enabled, ready: true };
}

function usePortalState(): PortalState {
  const [state, setState] = useState<PortalState>(
    cached ?? { enabled: PORTAL_ENABLED_DEFAULT, ready: false }
  );

  useEffect(() => {
    if (cached) {
      setState(cached);
      return;
    }

    let mounted = true;
    fetchPortalState().then(s => {
      if (!mounted) return;
      cached = s;
      setState(s);
      listeners.forEach(l => l(s));
    });

    const listener = (s: PortalState) => { if (mounted) setState(s); };
    listeners.add(listener);

    return () => {
      mounted = false;
      listeners.delete(listener);
    };
  }, []);

  return state;
}

export function usePortalEnabled(): boolean {
  return usePortalState().enabled;
}

export function usePortalReady(): boolean {
  return usePortalState().ready;
}

// Call this after toggling the setting in OPS to invalidate the cache immediately
export function invalidatePortalCache(newValue: boolean) {
  const s: PortalState = { enabled: newValue, ready: true };
  cached = s;
  listeners.forEach(l => l(s));
}
